import {
  ComponentLabelSource,
  GetCssClassNamesOption,
  getEmptyGraphModel,
  GraphModelShape,
} from '@ardoq/data-model';
import {
  APIComponentAttributes,
  APIComponentType,
  ArdoqId,
} from '@ardoq/api-types';
import { globalFilters as Filters } from 'collections/filters';
import { filterCache } from 'collections/filterCache';
import { graphModel$, getCurrentGraphModel } from 'modelInterface/graphModel$';
import { getCurrentComponentLabelFormattingSetting } from 'modelInterface/util';
import { components$ } from 'streams/components/components$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { loadedScenarioData$ } from 'loadedScenarioData$';
import {
  CanComponentHaveChildren,
  GetChildren,
  HasChildren,
  GetComponentColor,
  Get,
  GetComponentsByWorkspaceId,
  GetCssClassNames,
  GetFieldLabelAndValue,
  GetNameWithFieldLabelAndValue,
  GetFields,
  GetFullPathName,
  GetImage,
  GetLevel,
  GetName,
  GetParentId,
  GetRawLabel,
  GetRepresentationData,
  GetRootComponents,
  GetShape,
  GetSources,
  GetTargets,
  GetType,
  GetTypeName,
  GetWorkspaceId,
  HasParentChanged,
  IsComponent,
  IsExternallyManaged,
  IsExternallyMissing,
  IsIncludedInContextByFilter,
  IsScenarioContextComponent,
  GetComponentDisplayColorAsSVGAttributes,
  GetComponentAncetryIds,
  GetPossibleParents,
  GetPossibleTypes,
  ComponentInterfaceImplementation,
  isComponentIdIncludedInContextByFilter,
} from '@ardoq/component-interface';
import {
  componentOperations,
  modelOperations,
  toIds,
  usersOperations,
} from '@ardoq/core';
import { getComponentComparator } from '@ardoq/common-helpers';
import { componentSort$ } from 'streams/context/componentSort$';
import { enhancedScopeDiffData$ } from 'scope/enhancedScopeDiffData$';
import {
  getDefaultCSSColor,
  getFillAndStroke,
  sanitizeFilterColorClassName,
} from '@ardoq/color-helpers';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { getFeatureContext } from 'streams/featureContext$';
import { fieldUtils, resolveFilterColor } from '@ardoq/scope-data';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { fieldInterface } from '@ardoq/field-interface';
import { RIGID_TYPE_WARNING_MSG } from 'models/consts';

// NOTE:
// The purpose of BB interfaces is to avoid exposing the BB model. In practice
// this means you should __never__ return a BB model from these functions.

const resolveComponentType = (
  component: APIComponentAttributes
): APIComponentType | null => {
  const modelData = workspaceInterface.getModelData(component.rootWorkspace);
  if (!modelData) return null;
  return (
    modelOperations.getModelTypes(modelData).componentTypesById[
      component.typeId
    ] ?? null
  );
};

const getSources: GetSources = componentId =>
  graphModel.targetMap.get(componentId) || [];

const getTargets: GetTargets = componentId =>
  graphModel.sourceMap.get(componentId) || [];

const isIncludedInContextByFilter: IsIncludedInContextByFilter = (
  component,
  ctx,
  scopeSource
) => {
  const cached = Filters.getCachedFilterResult(component._id);
  if (cached !== undefined) return cached;
  const typeName =
    ctx?.componentTypeByComponentId[component._id]?.name ??
    getTypeName(component._id) ??
    '';
  return Filters.isComponentIncludedInContextByFilter(
    scopeSource ?? null,
    {
      attributes: component,
      fields: fieldInterface.getFieldsByComponentType(
        component.typeId,
        component.model
      ),
      typeName,
      incomingRefCount: getSources(component._id).length,
      outgoingRefCount: getTargets(component._id).length,
    },
    getCurrentGraphModel()
  );
};

const getCssClassNames: GetCssClassNames = (componentId, options) => {
  const component = get(componentId);
  if (!component) return null;
  const rawColor = filterCache.getColor(componentId);
  const sanitizedFilterColor = rawColor
    ? sanitizeFilterColorClassName(rawColor)
    : undefined;
  return componentOperations.getCssClassNames(
    component,
    resolveComponentType(component),
    sanitizedFilterColor,
    options
  );
};

// Drops the backbone `setComponentType()` self-heal that fires when `typeId`
// is missing. Server-loaded components always carry a typeId; only newly
// created in-memory components rely on the self-heal, and those flows already
// call `setComponentType` from `initialize`/`change:parent`.

const getType: GetType = componentId => {
  const component = get(componentId);
  return component ? resolveComponentType(component) : null;
};

const getTypeName: GetTypeName = componentId => {
  const type = getType(componentId);
  if (!type) return null;
  return type.name;
};
const getRepresentationData: GetRepresentationData = componentId => {
  const component = get(componentId);
  if (!component) return null;
  return componentOperations.getRepresentationData(
    component,
    resolveComponentType(component)
  );
};

const sortComponents = (
  components: APIComponentAttributes[]
): APIComponentAttributes[] => {
  const { sort, locale } = componentSort$.state;
  return [...components].sort(getComponentComparator(sort, locale));
};

const getChildren: GetChildren = componentId => {
  const children = components$.state.childrenByParent.get(componentId);
  if (!children) return [];
  return sortComponents(children);
};

const hasChildren: HasChildren = componentId =>
  Boolean(components$.state.childrenByParent.get(componentId)?.length);

const getSubtree = (
  componentId: ArdoqId,
  into: APIComponentAttributes[] = []
): APIComponentAttributes[] => {
  for (const child of getChildren(componentId)) {
    into.push(child);
    getSubtree(child._id, into);
  }
  return into;
};

const isWorkspaceEmpty = (workspaceId: ArdoqId) =>
  !getAllComponents().some(
    component => component.rootWorkspace === workspaceId
  );
const getRootComponents: GetRootComponents = workspaceId => {
  if (!workspaceInterface.workspaceExists(workspaceId)) return [];
  return sortComponents(
    componentOperations.getRootComponents(workspaceId, components$.state.models)
  );
};

const getRawLabel: GetRawLabel = componentId => {
  const component = get(componentId);
  if (!component) return null;
  const labelFilter = Filters.getCompLabelFilter()[0];
  const labelKey: string =
    labelFilter?.get('value') ?? ComponentLabelSource.NAME;
  const excludeTime = !labelFilter?.attributes?.includeTime;
  const fields = fieldInterface.getFieldsByComponentType(
    component.typeId,
    component.model
  );
  return componentOperations.getRawLabel({
    component,
    componentType: resolveComponentType(component),
    labelKey,
    excludeTime,
    fields,
    formatFieldValue: fieldInterface.format,
  });
};
const isComponent: IsComponent = componentId => get(componentId) !== null;

const getComponentColor: GetComponentColor = componentId => {
  const component = get(componentId);
  if (!component) return null;
  return componentOperations.getColor(
    component,
    resolveComponentType(component)
  );
};

const getImage: GetImage = componentId => {
  const component = get(componentId);
  if (!component) return null;
  return componentOperations.getImage(
    component,
    resolveComponentType(component)
  );
};

const getShape: GetShape = componentId => {
  const component = get(componentId);
  if (!component) return null;
  return componentOperations.getShape(
    component,
    resolveComponentType(component)
  );
};

const getWorkspaceId: GetWorkspaceId = componentId =>
  get(componentId)?.rootWorkspace ?? null;

const getFieldLabelAndValue: GetFieldLabelAndValue = (
  componentId,
  returnIfFormattingType = true,
  textAreaFormat
) => {
  const component = get(componentId);

  if (!component) {
    return null;
  }

  const activeCompLabelFilter = Filters.getCompLabelFilter();
  const currentSetting: string = activeCompLabelFilter[0]?.get('value');

  if (currentSetting === ComponentLabelSource.TYPE && !returnIfFormattingType) {
    return null;
  }

  const field = fieldInterface.getGlobalFieldByName(currentSetting);

  if (!field) {
    return null;
  }

  const fieldLabel = field.label || field.name;
  const fieldValue = (component as Record<string, unknown>)[currentSetting];
  const excludeTime = !activeCompLabelFilter[0]?.attributes?.includeTime;

  // i18nLocale$.state / orgUsers$.state OK here: imperative component label helper invoked
  // from one-shot callers (graph rendering, AG Grid value formatters).
  const formattedValue =
    fieldUtils.fieldAttributesToLabel({
      fieldAttributes: field,
      value: fieldValue,
      excludeTime,
      textAreaFormat,
      formatUserFieldFn: val =>
        usersOperations.formatUserField(orgUsers$.state.byId, val),
      i18nLocale: i18nLocale$.state,
    }) ??
    fieldValue ??
    null;

  if (!formattedValue && formattedValue !== 0) {
    return null;
  }

  if (!fieldLabel) {
    return `[${formattedValue}]`;
  }

  return `[${fieldLabel}: ${formattedValue}]`;
};

const getNameWithFieldLabelAndValue: GetNameWithFieldLabelAndValue =
  componentId => {
    const currentSetting = getCurrentComponentLabelFormattingSetting();
    if (currentSetting === ComponentLabelSource.NONE) {
      return null;
    }

    const name = getDisplayName(componentId);
    if (!name) {
      return null;
    }

    const fieldLabelAndValue = getFieldLabelAndValue(componentId, true, 'text');
    return `${name}${fieldLabelAndValue ? `\n${fieldLabelAndValue}` : ''}`;
  };

const getFields: GetFields = componentId => {
  const component = get(componentId);
  return component
    ? fieldInterface.getFieldsByComponentType(component.typeId, component.model)
    : [];
};

let graphModel: GraphModelShape = getEmptyGraphModel();
graphModel$.subscribe(graph => (graphModel = graph));

const getParentId: GetParentId = componentId =>
  get(componentId)?.parent ?? null;

const getLevel: GetLevel = componentId => {
  if (!get(componentId)) return -1;
  let level = 1;
  let parentId = getParentId(componentId);
  while (parentId && get(parentId)) {
    parentId = getParentId(parentId);
    level++;
  }
  return level;
};

const getManagedStatesForComponent = (componentId: ArdoqId) => {
  const component = get(componentId);
  if (!component) {
    return;
  }
  const jobIds = workspaceInterface
    .getWorkspaceIntegrations(component.rootWorkspace)
    .map(x => x._id);
  const managedData = component.ardoq?.persistent?.managed || {};
  return jobIds.filter(x => managedData[x]).map(x => managedData[x]);
};

const isExternallyManaged: IsExternallyManaged = componentId => {
  const activeManagedDefinitions = getManagedStatesForComponent(componentId);
  return Boolean(
    activeManagedDefinitions &&
    activeManagedDefinitions.find(x => x.seenInLastImport)
  );
};

const isExternallyMissing: IsExternallyMissing = componentId => {
  const activeManagedDefinitions = getManagedStatesForComponent(componentId);
  return Boolean(
    activeManagedDefinitions &&
    activeManagedDefinitions.length > 0 &&
    activeManagedDefinitions.every(x => !x.seenInLastImport)
  );
};

const isScenarioContextComponent: IsScenarioContextComponent = componentId =>
  loadedScenarioData$.state.contextComponents.has(componentId);

const getDisplayName: GetName = componentId => get(componentId)?.name ?? null;

const getFullPathName: GetFullPathName = componentId => {
  const component = get(componentId);
  if (!component) return null;
  const separator = ' / ';
  const name = component.name || '';
  const parentId = getParentId(componentId);
  const parentPath = parentId ? getFullPathName(parentId) : null;
  return parentPath ? `${parentPath}${separator}${name}` : name;
};

const get: Get = componentId => components$.state.byId[componentId] ?? null;

const getComponentsByWorkspaceId: GetComponentsByWorkspaceId = workspaceId =>
  workspaceInterface.workspaceExists(workspaceId)
    ? getAllComponents().filter(
        component => component.rootWorkspace === workspaceId
      )
    : [];

const getComponentHierarchy = (
  componentId: ArdoqId,
  getChildrenFn: (componentId: ArdoqId) => ArdoqId[] = id =>
    toIds(getChildren(id))
): Record<ArdoqId, { isFilteredOut: boolean; children?: ArdoqId[] }> => {
  const children = getChildrenFn(componentId);

  const isFilteredOut = !isComponentIdIncludedInContextByFilter(componentId);

  if (children.length === 0) {
    return isFilteredOut
      ? {}
      : {
          [componentId]: {
            isFilteredOut,
          },
        };
  }

  const hierarchy: Record<
    ArdoqId,
    { isFilteredOut: boolean; children?: ArdoqId[] }
  > = {};

  children.forEach(componentId => {
    const childHierarchy = getComponentHierarchy(componentId);

    for (const key in childHierarchy) {
      hierarchy[key] = childHierarchy[key];
    }
  });

  const isRemovedNode = isFilteredOut && children.every(id => !hierarchy[id]);

  if (isRemovedNode) {
    return hierarchy;
  }

  hierarchy[componentId] = {
    isFilteredOut,
    children: children.filter(id => hierarchy[id]),
  };

  return hierarchy;
};

const getComponentsHierarchyByWorkspaceIds = (
  workspaceIds: ArdoqId[]
): Record<ArdoqId, { isFilteredOut: boolean; children?: ArdoqId[] }> =>
  workspaceIds.reduce(
    (acc, workspaceId: ArdoqId) => ({
      ...acc,
      ...getComponentHierarchy(workspaceId, id => toIds(getRootComponents(id))),
    }),
    { root: { isFilteredOut: false, children: workspaceIds } }
  );

const canComponentHaveChildren: CanComponentHaveChildren = componentId => {
  const component = get(componentId);
  if (!component) return false;
  if (workspaceInterface.isFlexibleWorkspace(component.rootWorkspace)) {
    return true;
  }
  const type = resolveComponentType(component);
  return type ? Object.keys(type.children).length > 0 : false;
};

const getPossibleParents: GetPossibleParents = componentId => {
  const component = get(componentId);
  if (!component) return [];

  const workspaceComponents = getComponentsByWorkspaceId(
    component.rootWorkspace
  );
  const descendantIds = toIds(getSubtree(componentId));
  const isFlexible = workspaceInterface.isFlexibleWorkspace(
    component.rootWorkspace
  );
  const modelData = workspaceInterface.getModelData(component.rootWorkspace);
  const componentTypesById = modelData
    ? modelOperations.getModelTypes(modelData).componentTypesById
    : {};

  return componentOperations.getPossibleParents(
    component,
    workspaceComponents,
    descendantIds,
    isFlexible,
    componentTypesById
  );
};

const getPossibleTypes: GetPossibleTypes = componentId => {
  const component = get(componentId);
  if (!component) return [];

  const workspaceId = component.rootWorkspace;
  const modelData = workspaceInterface.getModelData(workspaceId);
  if (!modelData) return [];

  const componentTypes = workspaceInterface.getComponentTypes(workspaceId);

  if (modelData.flexible) {
    return componentTypes.map(t => ({ val: t.id, label: t.name }));
  }

  const childrenIds = getChildren(componentId);
  if (childrenIds.length > 0) {
    return [{ val: component.typeId, label: RIGID_TYPE_WARNING_MSG }];
  }

  const parentTypeId = component.parent
    ? (get(component.parent)?.typeId ?? null)
    : null;
  const allowedTypes = parentTypeId
    ? Object.values(
        componentTypes.find(t => t.id === parentTypeId)?.children ?? {}
      )
    : Object.values(modelData.root);
  return allowedTypes.map(t => ({ val: t.id, label: t.name }));
};

const hasParentChanged: HasParentChanged = componentId =>
  Boolean(
    enhancedScopeDiffData$.state.diffData?.components.update?.[componentId]
      ?.parent
  );

const resolveComponentBaseColor = (componentId: ArdoqId) => {
  const componentColor = componentInterface.getComponentColor(componentId);
  if (componentColor) {
    return componentColor;
  }
  const type = getType(componentId);
  return type ? type.color || getDefaultCSSColor(type.level) : null;
};

const getComponentColorAsSVGAttributes = (
  componentId: ArdoqId,
  { useAsBackgroundStyle }: GetCssClassNamesOption
) => {
  const baseColor = resolveComponentBaseColor(componentId);
  if (!baseColor) {
    return { fill: undefined, stroke: undefined };
  }
  return getFillAndStroke(baseColor, useAsBackgroundStyle);
};

/**
 * Returns the CSS color for the fill and stroke of the component, the "final" color that should be
 * displayed in the UI.
 *
 * If conditional formatting is active and matches the component, returns the filter color.
 * Otherwise, returns the component's base color (component color or type color).
 *
 * @deprecated Each call resolves the component and its model type from global
 * state, and `resolveComponentType` rebuilds `componentTypesById` by flattening
 * the type tree every time. In hot paths (per-node loops) this is expensive.
 *
 * Prefer the pure scope-data version:
 * `getComponentDisplayColorAsSVGAttributes` from `@ardoq/scope-data`
 * (`componentUtils.ts`), which takes `EnhancedScopeData` + `FilterColorData`
 * and does no global lookups.
 */
const getComponentDisplayColorAsSVGAttributes: GetComponentDisplayColorAsSVGAttributes =
  (componentId, options) => {
    const filterColor = filterCache.getColor(componentId);
    if (!filterColor) {
      return getComponentColorAsSVGAttributes(componentId, options);
    }

    // Get the cached version of the matching filter
    const filterVersion = filterCache.getFilterVersion(componentId);

    return resolveFilterColor(filterColor, filterVersion, options);
  };

/**
 * @returns an array with the given component id at the end, preceded by its parent id, its parent's parent id, et cetera
 *
 * @deprecated Walk `components$.byId` upwards in a pure helper in
 * `componentOperations` (`@ardoq/core`). See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const getComponentAncestryIds: GetComponentAncetryIds = componentId =>
  componentId
    ? [...getComponentAncestryIds(getParentId(componentId)), componentId]
    : [];

const hasWriteAccess = (componentId: ArdoqId) => {
  const component = get(componentId);
  if (!component) {
    return false;
  }

  return componentAccessControlOperation.canEditComponent({
    component,
    permissionContext: currentUserInterface.getPermissionContext(),
    featureContext: getFeatureContext(),
  });
};

const getAllComponents = (): APIComponentAttributes[] =>
  sortComponents(components$.state.models);

/**
 * `componentInterface` is a Backbone-era singleton: every member resolves the
 * component, and often its model and the active filters, from global mutable
 * state at call time.
 *
 * The app is stream-based now. Resolve components from `components$`
 * (`streams/components/components$.ts`) at a permitted boundary -- a `*$.ts`
 * file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread the
 * resolved data downstream as plain parameters.
 *
 * Most members here have a direct pure counterpart in `componentOperations`
 * (`@ardoq/core`), often under the same name -- `getCssClassNames`, `getColor`,
 * `getShape`, `getImage`, `getRawLabel`, `getRepresentationData`,
 * `canComponentHaveChildren`, `getPossibleParents`, `getRootComponents`,
 * `getFlatHierarchy`, `buildChildrenByParent`, `buildParentChangeCascade`. Type
 * resolution lives in `modelOperations` (`@ardoq/core`), the
 * `EnhancedScopeData` flavour in `componentUtils` /
 * `enhancedScopeDataOperations` (`@ardoq/scope-data`), and id extraction in
 * `entityOperations` (`toId` / `toIds` / `toIdSet`, `@ardoq/core`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const componentInterface: ComponentInterfaceImplementation = {
  /**
   * @deprecated Call `componentOperations.canComponentHaveChildren(component, componentTypesById, isFlexible)` (`@ardoq/core`) with types from `workspaceAndModel$`.
   */
  canComponentHaveChildren,
  /**
   * @deprecated Call `componentOperations.getPossibleParents(component, workspaceComponents, descendantIds, isFlexible, componentTypesById)` (`@ardoq/core`).
   */
  getPossibleParents,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).componentTypes` (`@ardoq/scope-data`), or `modelOperations.getComponentTypes(model)` (`@ardoq/core`).
   */
  getPossibleTypes,
  /**
   * @deprecated Read `components$.childrenByParent`, which `components$` builds with `componentOperations.buildChildrenByParent` (`@ardoq/core`).
   */
  getChildren,
  /**
   * @deprecated Read `components$.childrenByParent`, which `components$` builds with `componentOperations.buildChildrenByParent` (`@ardoq/core`).
   */
  hasChildren,
  /**
   * @deprecated Call `componentUtils.getComponentAncestryIds` or `enhancedScopeDataOperations.getAncestorIds` (`@ardoq/scope-data`).
   */
  getComponentAncestryIds,
  /**
   * @deprecated Call `componentOperations.getColor(component, componentType)` (`@ardoq/core`).
   */
  getComponentColor,
  /**
   * @deprecated Read `components$.byId[id]`.
   */
  get,
  /** @deprecated prefer the pure scope-data version `getComponentDisplayColorAsSVGAttributes` from `@ardoq/scope-data` (`componentUtils.ts`) */
  getComponentDisplayColorAsSVGAttributes,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) with `components$.childrenByParent`.
   */
  getComponentHierarchy,
  /**
   * @deprecated Read `components$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getComponentsByWorkspaceId,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) per workspace with `components$.childrenByParent`.
   */
  getComponentsHierarchyByWorkspaceIds,
  /**
   * @deprecated Call `componentOperations.getCssClassNames(component, componentType, sanitizedFilterColor, options)` (`@ardoq/core`), or `componentUtils.getComponentCSSClassNames` (`@ardoq/scope-data`).
   */
  getCssClassNames,
  /**
   * @deprecated Read `components$.byId[id]?.name`.
   */
  getDisplayName,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$`.
   */
  getFieldLabelAndValue,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentFields` (`@ardoq/scope-data`), or select from `fields$` with `fieldOperations` (`@ardoq/core`).
   */
  getFields,
  /**
   * @deprecated Call `scopeDataOperations.getAncestorBreadcrumbs` (`@ardoq/scope-data`), or walk `components$.byId` upwards.
   */
  getFullPathName,
  /**
   * @deprecated Call `componentOperations.getImage(component, componentType)` (`@ardoq/core`).
   */
  getImage,
  /**
   * @deprecated Call `componentUtils.getLevel(scopeData, componentId)` (`@ardoq/scope-data`), or walk `components$.byId` upwards.
   */
  getLevel,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$` and prepend the resolved name.
   */
  getNameWithFieldLabelAndValue,
  /**
   * @deprecated Read `components$.byId[id]?.parent`.
   */
  getParentId,
  /**
   * @deprecated Call `componentOperations.getRawLabel` (`@ardoq/core`) with the component resolved from `components$`.
   */
  getRawLabel,
  /**
   * @deprecated Call `componentOperations.getRepresentationData(component, componentType)` (`@ardoq/core`).
   */
  getRepresentationData,
  /**
   * @deprecated Call `componentOperations.getRootComponents(workspaceId, components)` (`@ardoq/core`) with `components$.models`.
   */
  getRootComponents,
  /**
   * @deprecated Call `componentOperations.getShape(component, componentType)` (`@ardoq/core`).
   */
  getShape,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `componentUtils.getSources(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  getSources,
  /**
   * @deprecated Call `componentOperations.getFlatHierarchy(roots, childrenByParent)` (`@ardoq/core`) with `components$.childrenByParent`.
   */
  getSubtree,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `componentUtils.getTargets(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  getTargets,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentTypeByComponentId` (`@ardoq/scope-data`), or `modelOperations.getComponentTypes(model).componentTypesById[typeId]` (`@ardoq/core`).
   */
  getType,
  /**
   * @deprecated Call `modelOperations.getComponentTypes(model)` (`@ardoq/core`) once per model emission; this member reflattens the type tree on every call.
   */
  resolveComponentType,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getComponentTypeByComponentId` (`@ardoq/scope-data`) and read `.name`.
   */
  getTypeName,
  /**
   * @deprecated Read `components$.byId[id]?.rootWorkspace`.
   */
  getWorkspaceId,
  /**
   * @deprecated Derive the diff in a reducer on `components$`; `componentOperations.buildParentChangeCascade` (`@ardoq/core`) is the pure counterpart of the cascade this triggers.
   */
  hasParentChanged,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the pure `workspaceAccessControlInterface.canEditWorkspace(permissionContext, workspaceId, activeScenario)`.
   */
  hasWriteAccess,
  /**
   * @deprecated Read `components$.byId[id]` for presence, or call `componentUtils.isComponent` (`@ardoq/scope-data`).
   */
  isComponent,
  /**
   * @deprecated Call `componentUtils.isExternallyManaged(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `componentUtils.isExternallyMissing(scopeData, componentId)` (`@ardoq/scope-data`).
   */
  isExternallyMissing,
  /**
   * @deprecated Join `includedInContext$` (`streams/filters/includedInContext$.ts`), or call the pure `isIncludedInContextByFilter` from `@ardoq/filter-helpers` with resolved filters.
   */
  isIncludedInContextByFilter,
  /**
   * @deprecated Call `enhancedScopeDataOperations.isScopeComponent` (`@ardoq/scope-data`), or join `activeScenario$` and use `activeScenarioOperations` (`@ardoq/core`).
   */
  isScenarioContextComponent,
  /**
   * @deprecated Call `workspaceUtils.isWorkspaceEmpty(scopeData, workspaceId)` (`@ardoq/scope-data`), or filter `components$.models`.
   */
  isWorkspaceEmpty,
  /**
   * @deprecated Read `components$.models`.
   */
  getAllComponents,
};
